import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import dayjs from 'dayjs';
import {
  DesignServices,
  DriveFileRenameOutline,
  FileDownload,
  Visibility,
} from '@mui/icons-material';
import { ARFilter } from '../ARFilter';
import {
  useGetARQualifications,
  AllQualificationsFilter,
  type ARQualificationData,
} from '../../graphql';
import {
  QUALIFICATION_CHECK_STATUS_TEXT,
  QUALIFICATION_CHECK_STATUS,
} from '../../constants';
import { AR_LIST_TEXT } from '../../lang';
import { getOptionsLabel } from '../../../Monitor/components';
import { programmeTypeOptions } from '../../../Application/components';
import {
  Alert,
  AdvancedTable,
  type AlertDetails,
  Button,
  type HeadCell,
  IconButton,
  Loading,
  pageRowsSelectOptions,
} from '../../../../components';
import {
  handleViewAsApplicant,
  postRequest,
  type PostRequestData,
} from '../../../../utils';

export type ARQualificationCheckListProps = {
  isAll: boolean;
  children?: React.ReactNode;
};

const enum EXPORT_PATH {
  QUALIFICATION_CHECK_PATH = `qualification-check`,
  VERIFICATION_STATUS_PATH = `verification-status`,
}

export const ARQualificationCheckList: React.FunctionComponent = () => {
  const [isAll, setIsAll] = useState(true);
  const handleSwitchTask = (all: boolean) => (): void => {
    setIsAll(all);
  };

  const buttonArea: React.ReactNode = (
    <div className="flex flex-row gap-[1rem]">
      <Button
        onClick={handleSwitchTask(true)}
        variant={isAll ? 'contained' : 'outlined'}
      >
        {'All'}
      </Button>
      <Button
        onClick={handleSwitchTask(false)}
        variant={isAll ? 'outlined' : 'contained'}
      >
        {'To be checked'}
      </Button>
    </div>
  );

  return (
    <ARQualificationCheckTable isAll={isAll} key={Number(isAll)}>
      {buttonArea}
    </ARQualificationCheckTable>
  );
};

export const ARQualificationCheckTable: React.FunctionComponent<
  ARQualificationCheckListProps
> = (props: ARQualificationCheckListProps) => {
  const { children, isAll } = props;
  const [page, setPage] = useState(1);
  const [selectRowsLimit, setSelectRowsLimit] = useState(
    pageRowsSelectOptions[0].value as number,
  );
  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'info',
  });
  const [checkedRows, setCheckedRows] = useState<number[]>([]); // to store all index of checked row(s)
  const [checkedAll, setCheckedAll] = useState<boolean>(false);

  const [filter, setFilter] = useState<any>(null);
  const [appliedFilter, setAppliedFilter] = useState<any>(null);

  const memoizedFilter = useMemo(() => {
    return { filter, appliedFilter };
  }, [filter, appliedFilter]);

  useEffect(() => {
    setFilter(memoizedFilter.filter);
    setAppliedFilter(memoizedFilter.appliedFilter);
  }, [memoizedFilter]);

  // query the paginated data
  const {
    arPaginatedQualifications,
    countryList: nationalityOptions,
    departmentsAndEntryYear,
    loading,
    error,
    refetch,
  } = useGetARQualifications(
    isAll ? { ...filter } : { ...AllQualificationsFilter, ...filter },
    {},
    { page, limit: selectRowsLimit },
  );

  const { data, totalPage, count } = arPaginatedQualifications;
  const visibleData: ARQualificationData[] = data;
  const pageCount: number = totalPage ?? 1;
  const recordsCount: number = count ?? 0;

  const dataCells: Array<HeadCell<ARQualificationData>> = [
    {
      id: 'applicationNo',
      label: AR_LIST_TEXT.APPLICATION_NO,
      isSortable: false,
      render: (row: ARQualificationData) => row.applicationNo,
    },
    {
      id: 'applicantName',
      label: AR_LIST_TEXT.APPLICANT_NAME,
      isSortable: false,
      render: (row: ARQualificationData) => row.applicantName,
    },
    {
      id: 'programmeType',
      label: AR_LIST_TEXT.PROG_TYPE,
      isSortable: false,
      render: (row: ARQualificationData) =>
        getOptionsLabel(programmeTypeOptions, row.programmeType),
    },
    {
      id: 'programmeCode',
      label: AR_LIST_TEXT.PROG_CODE,
      isSortable: false,
      render: (row: ARQualificationData) => row.programmeCode,
    },
    {
      id: 'entryYear',
      label: AR_LIST_TEXT.ENTRY_YEAR,
      isSortable: false,
      render: (row: ARQualificationData) => row.entryYear,
    },
    {
      id: 'applicantNationality',
      label: AR_LIST_TEXT.NATIONALITY,
      isSortable: false,
      render: (row: ARQualificationData) =>
        getOptionsLabel(nationalityOptions, row.applicantNationality),
    },
    {
      id: 'status',
      label: AR_LIST_TEXT.STATUS,
      isSortable: false,
      render: (row: ARQualificationData) =>
        QUALIFICATION_CHECK_STATUS_TEXT[
          row.status as keyof typeof QUALIFICATION_CHECK_STATUS_TEXT
        ],
    },
    {
      id: 'displayStatus',
      label: AR_LIST_TEXT.RELEASED,
      isSortable: false,
      render: (row: ARQualificationData) => {
        const hasReleased =
          row.status !== QUALIFICATION_CHECK_STATUS.CREATED &&
          row.status === row.displayStatus;
        const releasedRemind = hasReleased ? '\u2714' : '';

        return releasedRemind;
      },
    },
    {
      id: 'action',
      label: AR_LIST_TEXT.ACTION,
      isSortable: false,
      minWidth: 10,
      render: (row: ARQualificationData) => action(row),
    },
  ];

  const handleSwitchQualificationCheck =
    (applicationId: string) => (): void => {
      window.open(`/internal/ar-homepage/ar-process/${applicationId}?am=1`);
    };

  const handleViewMonitor =
    (applicationId: string): (() => void) =>
    () => {
      if (applicationId) window.open(`/internal/monitor/${applicationId}`);
    };

  const handleChangeCheckedIndex = (checkedIndex: number[]): void => {
    setCheckedRows(checkedIndex);
  };

  const handleChangeCheckAll = (checkAll: boolean): void => {
    setCheckedAll(checkAll);
  };

  const action = (row: ARQualificationData): React.ReactNode => {
    const { applicationId } = row;
    // no constrain to the edit action
    // even if passed qualification, still can enter form to edit
    return (
      <div className="flex justify-center items-center">
        <div className="grid grid-cols-2">
          <IconButton
            onClick={handleSwitchQualificationCheck(applicationId)}
            color="primary"
            icon={<DriveFileRenameOutline />}
          />

          <IconButton
            onClick={handleViewMonitor(applicationId)}
            color="primary"
            icon={<Visibility />}
          />

          <IconButton
            onClick={handleViewAsApplicant(applicationId, status)}
            title="View as Applicant"
            color="primary"
            icon={<DesignServices />}
          />
        </div>
      </div>
    );
  };

  // TODO: consider to move the table related functions to a separate file
  const handleRequestSort = (property: keyof ARQualificationData): void => {
    // TODO: Get the sorted data from the server
  };

  const handleChangePage = (event: unknown, newPage: number): void => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setSelectRowsLimit(parseInt(event.target.value));
    setPage(1);
    refetch();
  };

  const handleExportCSV = async (path: EXPORT_PATH): Promise<void> => {
    if (disableExport) return;
    const exportApplicationIds: string[] = checkedRows.map(rowIndex => {
      const { applicationId } = visibleData[rowIndex];
      return applicationId;
    });

    try {
      const postData: PostRequestData = {
        url: `/eadmin-operation/ar/export/${path}`,
        data: JSON.stringify({ applicationIds: exportApplicationIds }),
        responseType: 'blob',
      };

      const response = await postRequest(postData);

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8;',
      });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      const formattedDateTime = dayjs().format('DD/MM/YYYY_HH-mm-ss');
      link.setAttribute('download', `${path}-${formattedDateTime}.xlsx`);

      // Trigger the download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Revoke the URL to free up memory
      URL.revokeObjectURL(url);
    } catch (error) {
      handleOpenErrorAlert(error);
    }
  };

  const handleOpenErrorAlert = (error: any): void => {
    setAlertDetails({
      alertContent: 'Error Occurs.' + (error as Error)?.message,
      alertType: 'error',
    });
    setOpenAlert(true);
  };

  if (error) {
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent: `Error Occurs.` + (error as Error)?.message,
          alertType: 'error',
        }}
      />
    );
  }

  const disableExport = !checkedRows.length || !visibleData.length; // no application has been selected

  return (
    <>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      <Container>
        <ARFilter
          showQualificationCheck
          departmentsAndEntryYear={departmentsAndEntryYear}
          initialAppliedFilter={memoizedFilter.appliedFilter}
          isAllQualification={isAll}
          setFilter={(filter: any, appliedFilter: any) => {
            setFilter(filter);
            setAppliedFilter(appliedFilter);
          }}
        />
        {children}
        {loading ? (
          <Loading />
        ) : (
          <>
            <div
              className="flex flex-row items-center cursor-pointer gap-[0.25rem] pt-[0.25rem]"
              aria-disabled={disableExport}
            >
              <Button
                onClick={async () => {
                  await handleExportCSV(EXPORT_PATH.QUALIFICATION_CHECK_PATH);
                }}
                disabled={disableExport}
                className="!normal-case"
                startIcon={<FileDownload />}
              >
                {AR_LIST_TEXT.EXPORT_TEXT.QUALIFICATION_CHECK}
              </Button>

              <Button
                onClick={async () => {
                  await handleExportCSV(EXPORT_PATH.VERIFICATION_STATUS_PATH);
                }}
                disabled={disableExport}
                className="!normal-case"
                startIcon={<FileDownload />}
              >
                {AR_LIST_TEXT.EXPORT_TEXT.VERIFICATION_STATUS}
              </Button>
            </div>
            <AdvancedTable<ARQualificationData>
              showCheckBox
              checkBoxProps={{
                checkedIndex: checkedRows,
                onChangeCheckedIndex: handleChangeCheckedIndex,
                checkedAll,
                onCheckAll: handleChangeCheckAll,
              }}
              tableProps={{
                headCells: dataCells,
                data: visibleData,
                handleRequestSort,
              }}
              advancedPaginationProps={{
                page,
                count: pageCount,
                recordText: `${recordsCount} records`,
                rowsPerPageOptions: pageRowsSelectOptions,
                rowsPerPage: selectRowsLimit,
                onChangeRowsPerPage: handleChangeRowsPerPage,
              }}
              handleChangePage={handleChangePage}
            />
          </>
        )}
      </Container>
    </>
  );
};

const Container = styled.div`
  h1 {
    text-align: center;
    font-size: 2rem;
    margin: 2rem 0;
  }

  .pagination-area {
    position: sticky;
    bottom: 0;
    display: flex;
    flex-direction: row;
    justify-content: center;
    text-align: center;
    bottom: 0;
    width: 100%;
    padding: 0.5rem 0;
    background-color: #ffffff;
    margin-bottom: 10rem;
  }
`;
